import React, { useState, useEffect } from 'react';
import ChatBox from '../components/ChatBox'; 
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { 
  Shield, CheckCircle, XCircle, DollarSign, Users, 
  Activity, Search, Filter, FileText, RefreshCw, MessageSquare,
  ChevronDown, ChevronUp, Trash2, Eye, ExternalLink, AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom'; // Added for safety redirect

const AdminDashboard = () => {
  const { notify } = useNotification(); 
  const navigate = useNavigate(); // Hook for redirection
  const [activeTab, setActiveTab] = useState('overview');
  
  // Initialize with safe defaults (0 instead of null/undefined)
  const [stats, setStats] = useState({ total_revenue: 0, pending_leads: 0, active_setters: 0, total_leads: 0 });
  
  // --- Data States (Initialize as empty arrays) ---
  const [pendingLeads, setPendingLeads] = useState([]);
  const [chats, setChats] = useState([]); 
  const [setters, setSetters] = useState([]); 
  const [refundRequests, setRefundRequests] = useState([]); 
  const [completedLeads, setCompletedLeads] = useState([]); 

  // --- UI States ---
  const [loading, setLoading] = useState(true); // Start as true
  const [editingPrice, setEditingPrice] = useState({});
  const [expandedSetterId, setExpandedSetterId] = useState(null); 
  const [selectedLead, setSelectedLead] = useState(null); 
  
  const [activeChat, setActiveChat] = useState(null); 

  useEffect(() => {
    fetchAdminData();
  }, [activeTab]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const statsRes = await api.get('admin/stats/');
      setStats(statsRes.data || { total_revenue: 0, pending_leads: 0, active_setters: 0, total_leads: 0 });

      if (activeTab === 'qc') {
        const leadsRes = await api.get('admin/leads/pending/');
        setPendingLeads(leadsRes.data || []);
      } 
      else if (activeTab === 'support') {
        const chatRes = await api.get('admin/chats/');
        setChats(chatRes.data || []);
      }
      else if (activeTab === 'users') {
        const settersRes = await api.get('admin/users/setters/'); 
        setSetters(settersRes.data || []);
      }
      else if (activeTab === 'finance') {
        const refundsRes = await api.get('admin/finance/refunds/');
        setRefundRequests(refundsRes.data || []);
        
        const completedRes = await api.get('admin/finance/completed/');
        setCompletedLeads(completedRes.data || []);
      }

    } catch (error) {
      console.error("Admin API Error:", error);
      // Safety: Redirect to login if unauthorized
      if (error.response?.status === 401 || error.response?.status === 403) {
         navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLeadAction = async (id, action) => {
    const finalPrice = editingPrice[id];
    
    if (action === 'approve' && !finalPrice) {
      notify('error', "Please set a Final Price before approving.");
      return;
    }

    try {
      await api.post(`admin/leads/${id}/action/`, {
        action: action,
        price_company: finalPrice
      });
      // Refresh current view
      setPendingLeads(pendingLeads.filter(l => l.id !== id));
      fetchAdminData();
      notify('success', `Lead ${action === 'approve' ? 'Approved' : 'Rejected'} successfully`);
    } catch (error) {
      notify('error', "Action failed");
    }
  };

  const handleDeleteLead = async (leadId) => {
    if(!window.confirm("Are you sure you want to delete this lead? This cannot be undone.")) return;
    try {
        await api.delete(`admin/leads/${leadId}/`);
        fetchAdminData(); // Refresh the list
        notify('success', 'Lead deleted successfully.');
    } catch (error) {
        notify('error', 'Failed to delete lead.');
    }
  };

  const startChat = (leadId, companyId) => {
    setActiveTab('support');
    setActiveChat({ leadId, companyId });
    setSelectedLead(null); // Close modal if open
  };

  const getLeadCode = (id) => `#LD-${String(id).padStart(4, '0')}`;

  // --- CRITICAL FIX: RENDER LOADING SCREEN ---
  if (loading && !stats.total_leads) { // Only block if we have NO data yet
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 font-[Poppins]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex font-[Poppins] text-sm md:text-base">
      
      {/* --- LEAD DETAIL MODAL --- */}
      <AnimatePresence>
        {selectedLead && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm">
            <motion.div 
                initial={{ scale: 0.9, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }} 
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            >
                <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">{selectedLead.business_name}</h2>
                        <p className="text-primary font-bold">{getLeadCode(selectedLead.id)} • {selectedLead.category}</p>
                    </div>
                    <button onClick={() => setSelectedLead(null)} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200"><XCircle size={24} /></button>
                </div>
                
                <div className="p-6 space-y-6">
                    {/* Full Address Block (Admin View) */}
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                        <h3 className="font-bold text-blue-800 mb-2 flex items-center"><Shield size={16} className="mr-2"/> Admin & Setter View (Private)</h3>
                        <p><strong>Address:</strong> {selectedLead.address || `${selectedLead.street}, ${selectedLead.city}, ${selectedLead.state} ${selectedLead.zip_code}, ${selectedLead.country}`}</p>
                        <p><strong>Decision Maker:</strong> {selectedLead.decision_maker_name} ({selectedLead.decision_maker_role})</p>
                        <p><strong>Contact:</strong> {selectedLead.phone} • {selectedLead.email}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 border border-gray-100 rounded-xl">
                            <h4 className="font-bold text-gray-500 text-xs uppercase mb-2">Details</h4>
                            <p><strong>Facility:</strong> {selectedLead.facility_type} ({selectedLead.facility_size})</p>
                            <p><strong>Employees:</strong> {selectedLead.company_size}</p>
                            <p><strong>Frequency:</strong> {selectedLead.frequency}</p>
                            <p><strong>Appointment:</strong> {selectedLead.appointment_date} @ {selectedLead.appointment_time}</p>
                        </div>
                        <div className="p-4 border border-gray-100 rounded-xl">
                            <h4 className="font-bold text-gray-500 text-xs uppercase mb-2">Financials</h4>
                            <p><strong>Company Price:</strong> ${selectedLead.price_company}</p>
                            <p><strong>Setter Payout:</strong> ${selectedLead.payout_setter}</p>
                            <p><strong>Status:</strong> {selectedLead.status}</p>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-bold text-gray-800 mb-2">Scope of Work</h4>
                        <p className="text-gray-600 bg-gray-50 p-4 rounded-xl text-sm leading-relaxed">{selectedLead.scope_of_work}</p>
                    </div>

                    {/* Refund Specific Info */}
                    {selectedLead.refund_reason && (
                        <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                             <h4 className="font-bold text-red-800 mb-1">Refund Request Reason:</h4>
                             <p className="text-red-700 italic">"{selectedLead.refund_reason}"</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
                        {/* Start chat with Setter */}
                        <button 
                            onClick={() => startChat(selectedLead.id, null)} // Pass null companyId to imply Setter chat if backend supports
                            className="flex-1 py-3 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-900 transition flex items-center justify-center"
                        >
                            <MessageSquare size={18} className="mr-2" /> Chat with Setter
                        </button>
                        
                        {/* If purchased, option to chat with Company */}
                        {selectedLead.purchased_by && (
                             <button 
                                onClick={() => startChat(selectedLead.id, selectedLead.purchased_by)}
                                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center"
                            >
                                <MessageSquare size={18} className="mr-2" /> Chat with Company
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Sidebar Navigation */}
      <div className="w-20 md:w-64 bg-slate-900 text-white min-h-screen fixed z-40 transition-all duration-300">
        <div className="p-4 md:p-6 flex justify-center md:justify-start">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <Shield className="mr-0 md:mr-2 text-accent" /> <span className="hidden md:block">Admin</span>
          </h2>
        </div>
        <nav className="mt-6 space-y-2 px-2">
          <SidebarItem icon={<Activity />} label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
          <SidebarItem icon={<FileText />} label="Lead QC" active={activeTab === 'qc'} onClick={() => setActiveTab('qc')} badge={stats.pending_leads} />
          <SidebarItem icon={<MessageSquare />} label="Support" active={activeTab === 'support'} onClick={() => setActiveTab('support')} />
          <SidebarItem icon={<Users />} label="Users" active={activeTab === 'users'} onClick={() => setActiveTab('users')} />
          <SidebarItem icon={<DollarSign />} label="Finance" active={activeTab === 'finance'} onClick={() => setActiveTab('finance')} />
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 p-4 md:p-8 ml-20 md:ml-64 w-full">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            {activeTab === 'overview' && "Platform Overview"}
            {activeTab === 'qc' && "Lead Quality Control"}
            {activeTab === 'support' && "Support Center"}
            {activeTab === 'users' && "User Management"}
            {activeTab === 'finance' && "Payouts & Refunds"}
          </h1>
          <div className="flex items-center space-x-4">
            <button onClick={fetchAdminData} className="p-2 bg-white rounded-full shadow hover:bg-gray-50 text-gray-600" title="Force Refresh">
                <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
            </button>
            <div className="text-xs text-gray-500 font-bold bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200 uppercase tracking-wide hidden md:block">Superuser Mode</div>
          </div>
        </div>

        {/* --- TAB: OVERVIEW --- */}
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <StatCard label="Pending Approvals" value={stats.pending_leads} color="bg-orange-500" icon={<Activity />} />
            <StatCard label="Total Revenue" value={`$${stats.total_revenue}`} color="bg-green-600" icon={<DollarSign />} />
            <StatCard label="Active Setters" value={stats.active_setters} color="bg-blue-500" icon={<Users />} />
            <StatCard label="Total Leads" value={stats.total_leads} color="bg-purple-600" icon={<FileText />} />
          </motion.div>
        )}

        {/* --- TAB: LEAD QC (APPROVALS) --- */}
        {activeTab === 'qc' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="font-bold text-gray-700">Pending Review Queue ({pendingLeads?.length || 0})</h3>
              <div className="flex space-x-2">
                <button className="p-2 bg-white border rounded hover:bg-gray-50"><Filter size={18} /></button>
                <button className="p-2 bg-white border rounded hover:bg-gray-50"><Search size={18} /></button>
              </div>
            </div>
            
            {!pendingLeads || pendingLeads.length === 0 ? (
              <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                <CheckCircle size={48} className="text-green-200 mb-4" />
                <p>No leads pending approval.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[800px]">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600 text-sm uppercase tracking-wider">
                      <th className="p-4 font-semibold">Date</th>
                      <th className="p-4 font-semibold">Business / Proof</th>
                      <th className="p-4 font-semibold">Details</th>
                      <th className="p-4 font-semibold">Suggested Price</th>
                      <th className="p-4 font-semibold">Set Final Price</th>
                      <th className="p-4 font-semibold text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <AnimatePresence>
                      {pendingLeads.map((lead) => (
                        <motion.tr 
                          key={lead.id}
                          exit={{ opacity: 0, height: 0 }}
                          className="hover:bg-blue-50 transition-colors"
                        >
                          <td className="p-4 text-sm text-gray-500">{lead.appointment_date}</td>
                          <td className="p-4">
                            <div className="font-bold text-gray-800">{lead.business_name}</div>
                            <div className="text-xs text-gray-500">{lead.category}</div>
                            {lead.proof_file && (
                                <a href={lead.proof_file} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center mt-1">
                                <FileText size={12} className="ml-1" /> View Proof
                                </a>
                            )}
                          </td>
                          <td className="p-4 text-sm">
                            <button onClick={() => setSelectedLead(lead)} className="text-primary hover:underline font-bold text-xs mb-1">View Full Details</button>
                            <div><span className="font-bold">Contact:</span> {lead.decision_maker_name}</div>
                            <div className="truncate max-w-[200px]" title={lead.address}><span className="font-bold">Loc:</span> {lead.address}</div>
                          </td>
                          <td className="p-4 text-gray-500 font-mono">${lead.price_company}</td>
                          <td className="p-4">
                            <div className="flex items-center">
                              <span className="text-gray-500 mr-1">$</span>
                              <input 
                                type="number" 
                                className="w-24 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-primary focus:border-primary"
                                placeholder={lead.price_company}
                                onChange={(e) => setEditingPrice({...editingPrice, [lead.id]: e.target.value})}
                              />
                            </div>
                          </td>
                          <td className="p-4 text-center flex justify-center space-x-2">
                            <button 
                              onClick={() => handleLeadAction(lead.id, 'approve')}
                              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white p-2 rounded-full shadow-md transition-all"
                              title="Approve & Publish"
                            >
                              <CheckCircle size={20} />
                            </button>
                            <button 
                              onClick={() => handleLeadAction(lead.id, 'reject')}
                              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white p-2 rounded-full shadow-md transition-all"
                              title="Reject Lead"
                            >
                              <XCircle size={20} />
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* --- TAB: SUPPORT MESSAGES --- */}
        {activeTab === 'support' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[70vh] md:h-[700px]">
             {/* Conversation List */}
             <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col md:col-span-1">
                <div className="p-4 bg-gray-50 border-b font-bold text-gray-700 flex justify-between items-center">
                    <span>Inbox</span>
                    <span className="text-xs text-gray-400 font-normal">Data retained for 3 mos</span>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {chats.map(chat => (
                       <div 
                         key={chat.id} 
                         onClick={() => setActiveChat({ leadId: chat.lead_id, companyId: chat.company_id })} 
                         className={`p-4 border-b cursor-pointer transition ${activeChat?.leadId === chat.lead_id && activeChat?.companyId === chat.company_id ? 'bg-blue-50 border-l-4 border-l-primary' : 'hover:bg-gray-50'}`}
                       >
                          <div className="flex justify-between mb-1">
                             <span className="font-bold text-gray-900">{chat.company_name}</span>
                             <span className="text-[10px] text-gray-400">{new Date(chat.timestamp).toLocaleDateString()}</span>
                          </div>
                          {/* Unique Code Trigger */}
                          <div className="flex items-center gap-2 mb-1">
                              <span 
                                onClick={(e) => { e.stopPropagation(); setSelectedLead(chat.lead); }}
                                className="text-xs font-bold text-white bg-primary px-2 py-0.5 rounded hover:bg-blue-700 cursor-pointer"
                              >
                                {getLeadCode(chat.lead_id)}
                              </span>
                              <span className="text-xs text-gray-500 truncate">{chat.lead_title}</span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{chat.last_message}</p>
                       </div>
                    ))}
                </div>
             </div>
             
             {/* Admin Chat View Area */}
             <div className="bg-white rounded-xl shadow-lg border border-gray-200 md:col-span-2 relative overflow-hidden flex flex-col">
                {activeChat ? (
                   <div className="flex-1 relative bg-gray-50">
                       <div className="absolute inset-0">
                           <style>{`.admin-chat-wrapper .fixed { position: absolute !important; bottom: 0; right: 0; width: 100%; height: 100%; border: none; border-radius: 0; box-shadow: none; }`}</style>
                           <div className="admin-chat-wrapper w-full h-full">
                               <ChatBox 
                                   leadId={activeChat.leadId} 
                                   companyId={activeChat.companyId} 
                                   onClose={() => setActiveChat(null)} 
                               />
                           </div>
                       </div>
                   </div>
                ) : (
                   <div className="flex-1 flex flex-col items-center justify-center text-gray-300 bg-gray-50">
                       <MessageSquare size={64} className="mb-4 opacity-50" />
                       <p className="text-lg font-medium">Select a conversation</p>
                   </div>
                )}
             </div>
          </div>
        )}

        {/* --- TAB: USER MANAGEMENT (SETTERS) --- */}
        {activeTab === 'users' && (
            <div className="space-y-4">
                {setters.map(setter => (
                    <div key={setter.id} className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
                        {/* Setter Header */}
                        <div 
                            className="p-4 flex flex-col md:flex-row justify-between items-center cursor-pointer hover:bg-gray-50 transition"
                            onClick={() => setExpandedSetterId(expandedSetterId === setter.id ? null : setter.id)}
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">{setter.username.charAt(0).toUpperCase()}</div>
                                <div>
                                    <h3 className="font-bold text-gray-800">{setter.username}</h3>
                                    <p className="text-xs text-gray-500">{setter.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 mt-2 md:mt-0">
                                <div className="text-right">
                                    <p className="text-xs text-gray-400 uppercase font-bold">Earnings</p>
                                    <p className="font-bold text-green-600">${setter.total_earnings}</p>
                                </div>
                                {expandedSetterId === setter.id ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                            </div>
                        </div>

                        {/* Dropdown Lead List */}
                        <AnimatePresence>
                            {expandedSetterId === setter.id && (
                                <motion.div 
                                    initial={{ height: 0 }} 
                                    animate={{ height: 'auto' }} 
                                    exit={{ height: 0 }} 
                                    className="border-t border-gray-100 bg-gray-50 overflow-hidden"
                                >
                                    <div className="overflow-x-auto p-4">
                                        <table className="w-full text-left text-sm">
                                            <thead className="text-xs text-gray-400 uppercase">
                                                <tr>
                                                    <th className="px-4 py-2">Date</th>
                                                    <th className="px-4 py-2">Business</th>
                                                    <th className="px-4 py-2">Status</th>
                                                    <th className="px-4 py-2 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {setter.leads && setter.leads.map(lead => (
                                                    <tr key={lead.id} className="hover:bg-white">
                                                        <td className="px-4 py-3">{lead.date}</td>
                                                        <td className="px-4 py-3 font-medium">{lead.business_name}</td>
                                                        <td className="px-4 py-3">
                                                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                                lead.status === 'PURCHASED' ? 'bg-purple-100 text-purple-700' :
                                                                lead.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                                                                'bg-yellow-100 text-yellow-700'
                                                            }`}>{lead.status}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-right flex justify-end gap-2">
                                                            <button onClick={() => setSelectedLead(lead)} className="text-gray-400 hover:text-primary"><Eye size={16}/></button>
                                                            {/* Allow deleting ONLY if approved but not purchased */}
                                                            {lead.status === 'APPROVED' && (
                                                                <button onClick={() => handleDeleteLead(lead.id)} className="text-gray-400 hover:text-red-500"><Trash2 size={16}/></button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {(!setter.leads || setter.leads.length === 0) && (
                                                    <tr><td colSpan="4" className="text-center py-4 text-gray-400">No leads found.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        )}

        {/* --- TAB: FINANCE (REFUNDS & COMPLETED) --- */}
        {activeTab === 'finance' && (
            <div className="space-y-8">
                
                {/* 1. Refund Requests */}
                <div className="bg-white rounded-xl shadow-lg border border-red-100 overflow-hidden">
                    <div className="p-4 bg-red-50 border-b border-red-100 flex items-center">
                        <AlertTriangle className="text-red-500 mr-2" size={20}/>
                        <h3 className="font-bold text-red-800">Refund Requests</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-white text-gray-500">
                                <tr>
                                    <th className="p-4 text-left">Company</th>
                                    <th className="p-4 text-left">Lead</th>
                                    <th className="p-4 text-left">Reason</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {refundRequests.map(req => (
                                    <tr key={req.id} className="hover:bg-red-50/30">
                                        <td className="p-4 font-bold">{req.company_name}</td>
                                        <td className="p-4">{getLeadCode(req.lead_id)}</td>
                                        <td className="p-4 italic text-gray-600 truncate max-w-xs">{req.reason}</td>
                                        <td className="p-4 flex justify-end gap-2">
                                            <button 
                                                onClick={() => startChat(req.lead_id, req.company_id)}
                                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-200"
                                            >
                                                Chat Company
                                            </button>
                                            <button 
                                                onClick={() => setSelectedLead({...req.lead, refund_reason: req.reason})}
                                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-200"
                                            >
                                                View & Decide
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {refundRequests.length === 0 && <div className="p-6 text-center text-gray-400">No active refund requests.</div>}
                    </div>
                </div>

                {/* 2. Completed Leads */}
                <div className="bg-white rounded-xl shadow-lg border border-green-100 overflow-hidden">
                    <div className="p-4 bg-green-50 border-b border-green-100 flex items-center">
                        <CheckCircle className="text-green-600 mr-2" size={20}/>
                        <h3 className="font-bold text-green-800">Completed Jobs (Payout Ready)</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-white text-gray-500">
                                <tr>
                                    <th className="p-4 text-left">Lead Code</th>
                                    <th className="p-4 text-left">Setter</th>
                                    <th className="p-4 text-left">Payout Amount</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {completedLeads.map(lead => (
                                    <tr key={lead.id} className="hover:bg-green-50/30">
                                        <td className="p-4 font-mono font-bold text-gray-700">{getLeadCode(lead.id)}</td>
                                        <td className="p-4">{lead.setter_name}</td>
                                        <td className="p-4 font-bold text-green-600">${lead.payout_setter}</td>
                                        <td className="p-4 text-right">
                                            <button 
                                                onClick={() => setSelectedLead(lead)}
                                                className="px-4 py-2 bg-gray-800 text-white rounded-lg text-xs font-bold hover:bg-black transition flex items-center float-right"
                                            >
                                                <Eye size={14} className="mr-1"/> View & Chat Setter
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {completedLeads.length === 0 && <div className="p-6 text-center text-gray-400">No completed leads yet.</div>}
                    </div>
                </div>
            </div>
        )}

      </div>
    </div>
  );
};

// --- SUB COMPONENTS ---

const SidebarItem = ({ icon, label, active, onClick, badge }) => (
  <button 
    onClick={onClick}
    className={`w-full flex flex-col md:flex-row items-center justify-center md:justify-between px-2 md:px-6 py-3 md:py-4 transition-colors duration-200 border-l-4 ${
      active ? 'bg-gray-800 text-white border-accent' : 'text-gray-400 border-transparent hover:bg-gray-800 hover:text-white'
    }`}
  >
    <div className="flex flex-col md:flex-row items-center">
      <span className="mb-1 md:mb-0 md:mr-3">{icon}</span>
      <span className="font-medium text-[10px] md:text-sm">{label}</span>
    </div>
    {badge > 0 && (
      <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm hidden md:inline-block">{badge}</span>
    )}
  </button>
);

const StatCard = ({ label, value, color, icon }) => (
  <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-100 flex items-center hover:shadow-md transition">
    <div className={`${color} p-3 md:p-4 rounded-full text-white mr-3 md:mr-4 shadow-lg`}>
      {icon}
    </div>
    <div>
      <p className="text-gray-500 text-[10px] md:text-xs font-bold uppercase tracking-wider">{label}</p>
      <h3 className="text-xl md:text-2xl font-extrabold text-gray-800">{value}</h3>
    </div>
  </div>
);

export default AdminDashboard;